import * as React from 'react'
import { useState, useEffect } from "react"
import filesize from 'filesize';
import { toast } from 'react-toastify';
import { Fab, Grid } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { FileRejection, useDropzone } from 'react-dropzone';
import { EntryImportFileFieldList } from '../ImportFileFieldList';
import useEntryFileState from '../../features/Entry/stores/EntryFileState';
import { LoadingButton } from '@mui/lab';
import { useLang } from '~/hooks/useLang';
import './styles.scss';
import notify from '~/utils/notify';
interface ImportFileFieldProps {
  readonly dispatchId: number | null;
  readonly handleClickImport: () => void;
}
export interface FileProps {
  readonly file: File;
  readonly type: string;
  readonly name: string;
  readonly readableSize: string;
  readonly image: string | null;
}
export interface FileBase64Props {
  readonly fileContent: string | ArrayBuffer | null;
  readonly file: File;
}
export interface ImageBase64Props {
  readonly image: string | ArrayBuffer | null;
  readonly file: File;
}

// eslint-disable-next-line max-lines-per-function
export function ImportFileField({ handleClickImport, dispatchId }: ImportFileFieldProps) {
  const { sendFile, sendVideoChunk } = useEntryFileState();
  const { translate } = useLang();
  const [uploadedFiles, setUploadedFiles] = useState<FileProps>();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileBase64, setFileBase64] = useState<FileBase64Props>();
  const maxSizeVideo = 30000000;
  const maxSizeImage = 16000000;

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/jpg': [],
      'text/csv': [],
      'application/pdf': [],
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'application/vnd.oasis.opendocument.spreadsheet': [],
      'application/vnd.oasis.opendocument.text': [],
      'text/plain': [],
      'video/mp4': []
    },
    maxFiles: 1,
    onDropAccepted: submitFile,
    onDropRejected: rejectFiles,
    multiple: false,
    validator: sizeFileValidator
  });

  function sizeFileValidator(file) {
    if (file.type.includes("mp4")) {
      if (file.size > maxSizeVideo) {
        return {
          code: "size-video-too-large",
          message: translate('Size is larger than', { size: maxSizeVideo / 1000000 })
        };
      }
    } else if (file.size > maxSizeImage) {
      return {
        code: "size-image-too-large",
        message: translate('Size is larger than', { size: maxSizeImage / 1000000 })
      };
    }
    return null
  }

  useEffect(() => {
    if (fileRejections[0]?.errors[0]?.message) {
      notify({
        type: 'error',
        message: fileRejections[0]?.errors[0]?.message,
      })
    }
  }, [fileRejections[0]?.errors[0]?.message])

  useEffect(() => {
    if (fileBase64) {
      const file = fileBase64.fileContent as string
      setUploadedFiles({
        file: fileBase64.file,
        image: file.split(',')[1],
        name: fileBase64.file.name,
        readableSize: filesize(fileBase64.file.size),
        type: fileBase64.file.type.split('/')[1]
      })
    }

  }, [fileBase64])

  function rejectFiles(fileRejections: readonly FileRejection[]) {
    fileRejections.forEach(fileRejection => {
      fileRejection.errors.forEach(err => {
        if (err.code === 'file-invalid-type') {
          toast.error(translate('Enter a file of the following types:') + ' pdf, csv, jpg, jpeg, xls, xlsx, doc, docx, ods, odt, txt, mp4')
          return
        } else if (err.code === 'file-too-large') {
          toast.error(translate('File is larger than') + ` ${maxSizeImage / 1000000}MB`)
          return
        }
      })
    })
  }

  function submitFile(files: readonly File[]) {
    const file = files[0]
    const reader = new FileReader();
    reader.readAsDataURL(file)
    reader.addEventListener('loadend', () => {
      setFileBase64({ file, fileContent: reader.result });
    })
  }

  async function handleUpload(): Promise<void> {
    if (!uploadedFiles) return;
    const { image, type, name } = uploadedFiles
    setIsLoading(true)
    if (type === 'mp4') {
      await uploadFile(uploadedFiles)
    } else {
      await sendFile({ dispatchId, type, image, name })
    }
    setIsLoading(false)
    setUploadProgress(0)
    handleClickImport()
  }

  function createUID(precision) {
    const head = Date.now().toString(precision);
    const tail = Math.random().toString(precision).substring(2);

    return head + tail;
  }

  async function uploadChunk({ fileName, type, chunk, chunkIndex, totalChunks, currentUID }) {
    try {
      // aguardamos a requisição do chunk para controlar progresso
      const response = await sendVideoChunk({
        dispatchId,
        name: fileName,
        type: type,
        chunkIndex: chunkIndex,
        chunks: chunk,
        totalChunks: totalChunks,
        uid: currentUID,
      })

      // se retornar ok, atualiza progresso (chunkIndex é 0-based)
      if (response) {
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100)
        setUploadProgress(progress)
      }

      return response

    } catch (error) {
      console.log("ERRO: ", error)
      throw error
    }
  }

  async function uploadFile(file) {
    const chunkSize = 12 * 1024 * 1024; // 5 MB por chunk
    const totalChunks = Math.ceil(file.file.size / chunkSize);
    const fileName = file.name;
    const fileType = file.type;
    const currentUID = createUID(36)

    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.image.slice(i * chunkSize, (i + 1) * chunkSize);
      await uploadChunk({ fileName: fileName, type: fileType, chunk: chunk, chunkIndex: i, totalChunks, currentUID });
    }
  }


  return (
    <section className="container">
      <div {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        <p>
          {translate('Drag and Drop Image Here')}
        </p>
      </div>
      <footer>
        <aside>
          <h4>
            {translate('File')}
          </h4>
          {!!uploadedFiles && <EntryImportFileFieldList file={uploadedFiles} />}

        </aside>
        <Grid container direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item p={2}>

            <LoadingButton
              onClick={handleUpload}
              loading={isLoading}
              loadingIndicator={isLoading && uploadProgress > 0 ? `${uploadProgress}%` : undefined}
              variant="contained"
              size='small'
            >
              {translate('Save')}
            </LoadingButton>

          </Grid>
          <Fab
            size="small"
            onClick={handleClickImport}
            sx={{
              position: 'absolute',
              top: '-15px',
              right: '-2px',
              transform: 'scale(0.6)',
              color: '#black',
              backgroundColor: '#9e9e9e',
              "&:hover": { backgroundColor: "#6e6e6e" }
            }}
          >
            <ClearIcon />
          </Fab >
        </Grid>
      </footer>

    </section>

  );
}
